import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from '@/components/v4';
import {
    AccordionItem,
    Badge,
    Box,
    HStack,
    Heading,
    ListItem,
    Text,
    UnorderedList,
    useDisclosure,
} from '@chakra-ui/react';
import { useState } from 'react';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { MdDragIndicator } from 'react-icons/md';
import { useSelector } from 'react-redux';

const PlatformNumberWarning = ({
    isDragging = false,
    isDraggingGroup = false,
    dropZoneContent = null,
    hasPendingPlatforms = false,
    canCreateGroup = true,
    simRingEnabled = false,
    failoverEnabled = false,
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    const [promoModalType, setPromoModalType] = useState(null);

    const {
        isOpen: promoModalOpen,
        onOpen: onPromoModalOpen,
        onClose: onPromoModalClose,
    } = useDisclosure();

    const handleOpenModal = (type) => {
        setPromoModalType(type);
        onPromoModalOpen();
    };

    return (
        <AccordionItem key="2">
            <Box as="span" flex="1" textAlign="left">
                <HStack
                    marginTop="10px"
                    marginBottom="10px"
                    alignItems="flex-start">
                    <Box>
                        <Heading
                            sx={{
                                fontSize: '18px',
                            }}>
                            Assign Platforms & Set Priority
                        </Heading>
                        <Box mt={4} mb={2}>
                            <Text>
                                This number can be assigned to the platforms
                                below. The order determines the primary inbound
                                route, with the first in the list having the
                                highest priority.
                            </Text>
                        </Box>
                        <Box mt={4} mb={2}>
                            <UnorderedList pl={0} spacing={2} mt={2}>
                                <ListItem>
                                    <Text as="span" fontWeight="bold">
                                        Enable or Disable:{' '}
                                    </Text>
                                    <Text
                                        as="span"
                                        color={
                                            darkMode ? 'gray.300' : 'inherit'
                                        }>
                                        Manage each Platform Number below
                                    </Text>
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="bold">
                                        Re-order Priority:{' '}
                                    </Text>

                                    <Text
                                        as="span"
                                        color={
                                            darkMode ? 'gray.300' : 'inherit'
                                        }>
                                        Drag
                                        <Box
                                            as="span"
                                            display="inline-flex"
                                            verticalAlign="middle"
                                            mx="1">
                                            <MdDragIndicator size="20px" />
                                        </Box>
                                        and drop to change the order
                                        {failoverEnabled
                                            ? `
                                            of inbound call and failover routes.
                                            The first active platform shall act
                                            as primary, failing down the list in
                                            event of any call routing issues.
                                          `
                                            : '. '}
                                    </Text>

                                    {simRingEnabled && (
                                        <Text
                                            as="span"
                                            color={
                                                darkMode
                                                    ? 'white.500'
                                                    : 'inherit'
                                            }
                                            fontWeight="bold">
                                            Remember to set the order of any
                                            Branch Routing Groups created.
                                        </Text>
                                    )}
                                </ListItem>
                                {simRingEnabled && (
                                    <ListItem>
                                        <Text as="span" fontWeight="bold">
                                            Create Branch Routing Groups:{' '}
                                        </Text>
                                        <Text
                                            as="span"
                                            color={
                                                darkMode
                                                    ? 'gray.300'
                                                    : 'inherit'
                                            }>
                                            Drag platforms together to associate
                                            them into a Branch Routing Group.
                                            You may create multiple Branch
                                            Routing Groups and re-order as
                                            required. Use the status toggle for
                                            each platform.
                                        </Text>
                                    </ListItem>
                                )}
                            </UnorderedList>
                            {!failoverEnabled && (
                                <Box
                                    mt={3}
                                    p={3}
                                    bg={darkMode ? 'gray.700' : 'gray.100'}
                                    borderRadius="md"
                                    fontSize="sm">
                                    <Text>
                                        Your account has Failover Routing
                                        disabled. Only the first active item in
                                        the list shall receive inbound calls.
                                    </Text>
                                </Box>
                            )}

                            <Box
                                visibility={
                                    (isDragging || hasPendingPlatforms) &&
                                    simRingEnabled &&
                                    canCreateGroup &&
                                    !isDraggingGroup
                                        ? 'visible'
                                        : 'hidden'
                                }
                                height={
                                    (isDragging || hasPendingPlatforms) &&
                                    simRingEnabled &&
                                    canCreateGroup &&
                                    !isDraggingGroup
                                        ? 'auto'
                                        : '0'
                                }
                                overflow="hidden"
                                opacity={
                                    (isDragging || hasPendingPlatforms) &&
                                    simRingEnabled &&
                                    canCreateGroup &&
                                    !isDraggingGroup
                                        ? 1
                                        : 0
                                }
                                transition="opacity 0.15s ease-in-out, height 0.15s ease-in-out">
                                {simRingEnabled &&
                                    canCreateGroup &&
                                    dropZoneContent}
                            </Box>

                            <Box
                                visibility={
                                    !isDragging &&
                                    !hasPendingPlatforms &&
                                    (failoverEnabled || simRingEnabled)
                                        ? 'visible'
                                        : 'hidden'
                                }
                                height={
                                    !isDragging &&
                                    !hasPendingPlatforms &&
                                    (failoverEnabled || simRingEnabled)
                                        ? 'auto'
                                        : '0'
                                }
                                overflow="hidden"
                                opacity={
                                    !isDragging &&
                                    !hasPendingPlatforms &&
                                    (failoverEnabled || simRingEnabled)
                                        ? 1
                                        : 0
                                }
                                transition="opacity 0.15s ease-in-out, height 0.15s ease-in-out">
                                {(failoverEnabled || simRingEnabled) && (
                                    <HStack spacing={4} mt={4}>
                                        <Box
                                            flex={1}
                                            p={4}
                                            minH={100}
                                            border="2px solid"
                                            borderColor="blue.300"
                                            borderRadius="md"
                                            cursor="pointer"
                                            onClick={() =>
                                                handleOpenModal('failover')
                                            }
                                            bg={
                                                darkMode
                                                    ? 'dark.bgDark'
                                                    : 'transparent'
                                            }>
                                            <HStack spacing={2}>
                                                <IoMdInformationCircleOutline
                                                    size="24px"
                                                    color="#3182CE"
                                                />
                                                <Box>
                                                    <Text
                                                        fontWeight="semibold"
                                                        fontSize="md">
                                                        Failover Routing
                                                    </Text>
                                                    <Text
                                                        fontSize="sm"
                                                        color={
                                                            darkMode
                                                                ? 'gray.400'
                                                                : 'gray.600'
                                                        }>
                                                        Learn more about
                                                        automatic backup routing
                                                    </Text>
                                                </Box>
                                            </HStack>
                                        </Box>

                                        <Box
                                            flex={1}
                                            p={4}
                                            minH={100}
                                            border="2px solid"
                                            borderColor="blue.300"
                                            borderRadius="md"
                                            cursor="pointer"
                                            onClick={() =>
                                                handleOpenModal('branchRouting')
                                            }
                                            bg={
                                                darkMode
                                                    ? 'dark.bgDark'
                                                    : 'transparent'
                                            }>
                                            <HStack spacing={2}>
                                                <IoMdInformationCircleOutline
                                                    size="24px"
                                                    color="#3182CE"
                                                />
                                                <Box>
                                                    <Text
                                                        fontWeight="semibold"
                                                        fontSize="md">
                                                        Branch Routing Groups
                                                    </Text>
                                                    <Text
                                                        fontSize="sm"
                                                        color={
                                                            darkMode
                                                                ? 'gray.400'
                                                                : 'gray.600'
                                                        }>
                                                        Learn more about multi
                                                        platform calling
                                                    </Text>
                                                </Box>
                                            </HStack>
                                        </Box>
                                    </HStack>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </HStack>
            </Box>

            {/* Promotional Modal */}
            <Modal isOpen={promoModalOpen} onClose={onPromoModalClose}>
                <ModalOverlay />
                <ModalContent maxW="2xl">
                    <ModalHeader>
                        <HStack
                            justify="space-between"
                            align="flex-start"
                            spacing={4}>
                            <HStack spacing={2}>
                                <IoMdInformationCircleOutline
                                    size="24px"
                                    color="#3182CE"
                                />
                                <Text>
                                    {promoModalType === 'failover'
                                        ? 'Failover Routing'
                                        : 'Branch Routing Groups'}
                                </Text>
                            </HStack>
                            <Badge
                                colorScheme={
                                    promoModalType === 'failover'
                                        ? failoverEnabled
                                            ? 'green'
                                            : 'gray'
                                        : simRingEnabled
                                          ? 'green'
                                          : 'gray'
                                }
                                fontSize="sm"
                                px={4}
                                py={1.5}
                                borderRadius="full">
                                {promoModalType === 'failover'
                                    ? failoverEnabled
                                        ? 'Enabled'
                                        : 'Disabled'
                                    : simRingEnabled
                                      ? 'Enabled'
                                      : 'Disabled'}
                            </Badge>
                        </HStack>
                    </ModalHeader>
                    <ModalBody>
                        {/* Video Demo Placeholder NOTE: comment until there is a video */}
                        {/* <Box */}
                        {/*     aspectRatio={16 / 9} */}
                        {/*     bg="gray.100" */}
                        {/*     borderRadius="lg" */}
                        {/*     display="flex" */}
                        {/*     alignItems="center" */}
                        {/*     justifyContent="center" */}
                        {/*     mb={4}> */}
                        {/*     <Box textAlign="center" color="gray.500"> */}
                        {/*         <Text fontSize="4xl" mb={2}> */}
                        {/*             ▶️ */}
                        {/*         </Text> */}
                        {/*         <Text fontSize="sm">Feature Demo Video</Text> */}
                        {/*     </Box> */}
                        {/* </Box> */}

                        {promoModalType === 'failover' ? (
                            <>
                                <Text mb={4}>
                                    <Text as="span" fontWeight="bold">
                                        Failover Routing
                                    </Text>{' '}
                                    ensures your calls always reach their
                                    destination. If the primary platform fails
                                    or is unavailable, calls automatically route
                                    to the next platform in your priority list.
                                </Text>
                                <Text fontWeight="bold" fontSize="sm" mb={2}>
                                    Key Benefits:
                                </Text>
                                <UnorderedList fontSize="sm" spacing={1} mb={4}>
                                    <ListItem>
                                        Minimize missed calls with automatic
                                        backup routing
                                    </ListItem>
                                    <ListItem>
                                        Set custom priority order for your
                                        platforms
                                    </ListItem>
                                    <ListItem>
                                        Ensure business continuity during
                                        outages
                                    </ListItem>
                                    <ListItem>
                                        No manual intervention required
                                    </ListItem>
                                </UnorderedList>
                            </>
                        ) : (
                            <>
                                <Text mb={4}>
                                    <Text as="span" fontWeight="bold">
                                        Branch Routing Groups
                                    </Text>{' '}
                                    allow you to ring multiple platforms at the
                                    same time, ensuring the fastest response
                                    time and maximum availability for incoming
                                    calls.
                                </Text>
                                <Text fontWeight="bold" fontSize="sm" mb={2}>
                                    Key Benefits:
                                </Text>
                                <UnorderedList fontSize="sm" spacing={1} mb={4}>
                                    <ListItem>
                                        Answer calls faster by ringing all
                                        platforms at once
                                    </ListItem>
                                    <ListItem>
                                        Create groups of platforms that ring
                                        together
                                    </ListItem>
                                    <ListItem>
                                        Ideal for team collaboration and
                                        coverage
                                    </ListItem>
                                    <ListItem>
                                        First to answer wins - others
                                        automatically stop ringing
                                    </ListItem>
                                </UnorderedList>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <HStack spacing={2} justify="flex-end">
                            <Button minW="120px" onClick={onPromoModalClose}>
                                OK
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </AccordionItem>
    );
};

export default PlatformNumberWarning;
